import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { MessageBubble } from '../agents/MessageBubble';
import { TypingIndicator } from '../agents/TypingIndicator';
import { MessageSquare, Send, Sparkles, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

interface FirstQuestionStepProps {
  data?: any;
  onNext: (data?: any) => void;
  onBack: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function FirstQuestionStep({ data, onNext, onBack, isFirst, isLast }: FirstQuestionStepProps) {
  const [question, setQuestion] = useState(data?.question || '');
  const [isAsking, setIsAsking] = useState(false);
  const [hasAsked, setHasAsked] = useState(false);
  
  // Get response from data prop (set by OnboardingPage after AI call)
  const response = data?.response || '';
  const hasError = data?.error || false;

  const suggestedQuestions = [
    "What are my top performing marketing channels?",
    "How can I improve my website conversion rate?",
    "What content performs best with my audience?",
    "Which traffic sources drive the most valuable users?",
    "How has my organic search performance changed recently?"
  ];

  // Reset asking state when response comes back or error occurs
  useEffect(() => {
    if ((response || hasError) && isAsking) {
      setIsAsking(false);
    }
  }, [response, hasError, isAsking]);

  const handleAskQuestion = async () => {
    if (!question.trim()) return;

    setIsAsking(true);
    setHasAsked(true);
    
    // Send question to OnboardingPage which will handle the AI call
    onNext({ question, response: null });
  };

  const handleNext = () => {
    onNext({ question, response, completed: true });
  };

  const handleSkip = () => {
    onNext({ skipped: true });
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
          <MessageSquare className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Ask Your First Question
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Try out our AI marketing agent with a question about your business
        </p>
      </div>

      <div className="space-y-6">
        {!response && !isAsking && !hasError && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                What would you like to know about your marketing performance?
              </label>
              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask me anything about your marketing data..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows={3}
                />
                <Button
                  onClick={handleAskQuestion}
                  disabled={!question.trim()}
                  size="sm"
                  className="absolute bottom-3 right-3"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Ask
                </Button>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Or try one of these suggested questions:
              </p>
              <div className="space-y-2">
                {suggestedQuestions.map((suggestedQuestion, index) => (
                  <button
                    key={index}
                    onClick={() => setQuestion(suggestedQuestion)}
                    className="w-full text-left p-3 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg transition-colors"
                  >
                    {suggestedQuestion}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {isAsking && (
          <div className="space-y-4">
            <div className="border rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
              <div className="flex items-center mb-3">
                <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Chat Preview</span>
              </div>
              
              {/* Chat Interface with Loading */}
              <div className="space-y-3 bg-white dark:bg-gray-800 rounded-lg p-3 max-h-96 overflow-y-auto">
                {/* User Message */}
                <MessageBubble
                  message={{
                    id: 'onboarding-user-message',
                    content: question,
                    type: 'user',
                    timestamp: new Date().toISOString()
                  }}
                  onFollowUpQuestion={() => {}}
                />
                
                {/* Typing Indicator */}
                <TypingIndicator />
              </div>
              
              <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex items-center justify-center space-x-3">
                  <Loader2 className="w-5 h-5 text-purple-600 dark:text-purple-400 animate-spin" />
                  <div className="text-center">
                    <p className="text-purple-700 dark:text-purple-300 font-medium text-sm">
                      AI Agent is analyzing your question...
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      This may take a few moments
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasError && (
          <div className="space-y-4">
            <div className="border rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
              <div className="flex items-center mb-3">
                <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Chat Preview</span>
              </div>
              
              {/* Chat Interface with Error */}
              <div className="space-y-3 bg-white dark:bg-gray-800 rounded-lg p-3 max-h-96 overflow-y-auto">
                {/* User Message */}
                <MessageBubble
                  message={{
                    id: 'onboarding-user-message',
                    content: question,
                    type: 'user',
                    timestamp: new Date().toISOString()
                  }}
                  onFollowUpQuestion={() => {}}
                />
              </div>
              
              <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-center mb-3">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-300">Error:</span>
                </div>
                <p className="text-red-800 dark:text-red-200 text-sm mb-4">
                  Sorry, we couldn't get a response from the AI agent right now. This might be because your analytics aren't connected yet or there was a temporary issue.
                </p>
                <div className="flex space-x-3">
                  <Button
                    onClick={handleAskQuestion}
                    size="sm"
                    variant="outline"
                    className="border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={handleSkip}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 dark:text-red-400"
                  >
                    Skip for now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {response && (
          <div className="space-y-4">
            <div className="border rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
              <div className="flex items-center mb-3">
                <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Chat Preview</span>
              </div>
              
              {/* Chat Interface */}
              <div className="space-y-3 bg-white dark:bg-gray-800 rounded-lg p-3 max-h-96 overflow-y-auto">
                {/* User Message */}
                <MessageBubble
                  message={{
                    id: 'onboarding-user-message',
                    content: question,
                    type: 'user',
                    timestamp: new Date().toISOString()
                  }}
                  onFollowUpQuestion={() => {}}
                />
                
                {/* AI Response */}
                <MessageBubble
                  message={{
                    id: 'onboarding-ai-response',
                    content: response,
                    type: 'assistant',
                    timestamp: new Date().toISOString()
                  }}
                  onFollowUpQuestion={() => {}}
                />
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                🎉 Great! Your AI agent is ready to help with more insights.
              </p>
              <Button onClick={handleNext} size="lg">
                Continue to Chat
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          {!response && !isAsking && !hasError && (
            <Button variant="ghost" onClick={handleSkip}>
              Skip for now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} 